/**
 * The categories every product is filed under.
 *
 * These were two Prisma enums, so putting "Grooming tools" in the shop meant a schema
 * migration and a deploy — for a merchandising decision. They are rows now, and this is
 * where they are made.
 *
 * The screen is arranged around the one thing that makes a category dangerous to touch:
 * its code is written into every product row that has ever used it. So the code is shown
 * and never editable, the label is editable and renames the category everywhere at once,
 * and what is filed under each one is a column — because "delete Treats" and "delete the
 * ten listings nobody can find any more" are the same click otherwise.
 *
 * Like the fees screen, this is a settings surface rather than a queryable list: a dozen
 * rows, a fixed set, every one an editable control. It takes the blueprint's layout, tiles
 * and panels; a DataTable's paging and column preferences would be furniture over a form.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Boxes,
  Layers,
  Loader2,
  Pencil,
  Percent,
  Image as ImageIcon,
  Plus,
  Save,
  Store,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { AuthImage } from '@/components/auth-image';
import { DashboardLayout } from '@/components/dashboard-layout';
import { DetailPanel, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import { notify } from '@/components/ui/toaster';
import {
  use_create_category,
  use_upload_category_icon,
  use_delete_category,
  use_product_categories,
  use_update_category,
} from '@/features/product-categories/hooks';
import {
  VERTICAL_LABEL,
  key_from_label,
  usage_summary,
  usage_total,
  type CategoryWithUsage,
  type Vertical,
} from '@/features/product-categories/types';

const VERTICALS: Vertical[] = ['FOOD', 'PHARMACY'];

export function ProductCategoryPage() {
  const { data: categories, isLoading, error, refetch } = use_product_categories();
  const [creating, set_creating] = useState<Vertical | null>(null);
  const [editing, set_editing] = useState<string | null>(null);

  const metrics = useMemo(() => build_metrics(categories), [categories]);

  return (
    <DashboardLayout
      title="Product categories"
      subtitle="What every product in the shop is filed under, and what each one charges"
      stats={<MetricStrip metrics={metrics} />}
    >
      {error ? (
        <ErrorState
          title="Could not load the categories"
          description="Nothing has been changed. Try again."
          action={
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      ) : (
        <div className="grid gap-gutter">
          {VERTICALS.map((vertical) => {
            const rows = (categories ?? []).filter((c) => c.vertical === vertical);
            const live = rows.filter((c) => c.is_active).length;
            return (
              <DetailPanel
                key={vertical}
                title={VERTICAL_LABEL[vertical]}
                icon={Boxes}
                description={
                  categories
                    ? `${live} in use${rows.length > live ? `, ${rows.length - live} retired` : ''} · vendors pick from these when they list`
                    : undefined
                }
                body_class="p-0"
                test_id={`categories-${vertical}`}
                actions={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      set_editing(null);
                      set_creating(creating === vertical ? null : vertical);
                    }}
                    data-testid={`add-category-${vertical}`}
                  >
                    {creating === vertical ? (
                      <>
                        <X className="mr-1 h-4 w-4" /> Close
                      </>
                    ) : (
                      <>
                        <Plus className="mr-1 h-4 w-4" /> New category
                      </>
                    )}
                  </Button>
                }
              >
                {isLoading || !categories ? (
                  <div className="p-3.5">
                    <PanelSkeleton rows={4} />
                  </div>
                ) : (
                  <>
                    {creating === vertical && (
                      <CreateRow vertical={vertical} on_done={() => set_creating(null)} />
                    )}
                    <ul className="divide-y divide-border">
                      {rows.map((c) => (
                        <CategoryRow
                          key={c.id}
                          category={c}
                          open={editing === c.id}
                          on_open={(next) => {
                            set_creating(null);
                            set_editing(next ? c.id : null);
                          }}
                        />
                      ))}
                      {rows.length === 0 && (
                        <li className="px-3.5 py-6 text-center text-data text-muted-foreground">
                          No categories yet — nothing can be listed in this shop until there is
                          one.
                        </li>
                      )}
                    </ul>
                  </>
                )}
              </DetailPanel>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}

function build_metrics(categories: CategoryWithUsage[] | undefined): Metric[] {
  const live = categories?.filter((c) => c.is_active) ?? [];
  const retired = categories?.filter((c) => !c.is_active) ?? [];
  const empty = live.filter((c) => usage_total(c) === 0);
  const listings = categories?.reduce((n, c) => n + c.listings, 0) ?? 0;

  return [
    {
      key: 'live',
      label: 'Categories in use',
      value: categories ? String(live.length) : undefined,
      hint: retired.length ? `${retired.length} retired` : 'None retired',
      icon: Layers,
      loading: !categories,
    },
    {
      key: 'listings',
      label: 'Products filed',
      value: categories ? listings.toLocaleString('en-IN') : undefined,
      hint: 'Vendor listings, all categories',
      icon: Store,
      loading: !categories,
    },
    {
      key: 'no_icon',
      label: 'Missing an icon',
      /*
       * Worth its own tile because it is work, and work that is invisible otherwise: every
       * category predating icons renders a placeholder in the shop until somebody fills it.
       */
      value: categories ? String(categories.filter((c) => !c.icon_path).length) : undefined,
      hint: categories?.every((c) => c.icon_path) ? 'Every category has one' : 'Shows a placeholder in the shop',
      tone: categories?.some((c) => !c.icon_path) ? 'warning' : 'default',
      icon: ImageIcon,
      loading: !categories,
    },
    {
      key: 'empty',
      label: 'Nothing in them',
      // The only categories that can actually be deleted, and usually the ones that were
      // added speculatively and never used.
      value: categories ? String(empty.length) : undefined,
      hint: empty.length ? 'Safe to delete' : 'Every category is in use',
      tone: empty.length ? 'warning' : 'default',
      icon: Boxes,
      loading: !categories,
    },
    {
      key: 'fees',
      label: 'Fees',
      value: categories ? 'Per category' : undefined,
      hint: 'Set what each one charges',
      to: '/platform-fees',
      icon: Percent,
      loading: !categories,
    },
  ];
}

/* --------------------------------- one row -------------------------------- */

function CategoryRow({
  category,
  open,
  on_open,
}: {
  category: CategoryWithUsage;
  open: boolean;
  on_open: (next: boolean) => void;
}) {
  const update = use_update_category();
  const remove = use_delete_category();
  const used = usage_total(category);

  async function set_active(is_active: boolean) {
    try {
      await update.mutateAsync({ id: category.id, input: { is_active } });
      notify.success(is_active ? `${category.label} is back` : `${category.label} retired`, {
        description: is_active
          ? 'Vendors can file products under it again.'
          : `${usage_summary(category)} — all of it keeps selling, but nothing new can be filed here.`,
      });
    } catch (err) {
      notify.error(err, 'Could not change this category.');
    }
  }

  async function destroy() {
    try {
      await remove.mutateAsync(category.id);
      notify.success(`${category.label} deleted`, {
        description: 'Its fee rule went with it.',
      });
    } catch (err) {
      notify.error(err, 'Could not delete this category.');
    }
  }

  return (
    <li data-testid={`category-row-${category.vertical}-${category.key}`}>
      <div className="grid items-center gap-x-4 gap-y-2 px-3.5 py-3 lg:grid-cols-[2.5rem_16rem_1fr_auto]">
        {/*
          The artwork, or a gap that says it is missing. A blank square would read as "loading"
          or as a broken image; the outline and the word say a person still has to do something.
        */}
        {category.icon_path ? (
          <AuthImage
            path={category.icon_path}
            alt=""
            className="h-9 w-9 rounded-input object-cover"
            data-testid={`category-icon-${category.key}`}
          />
        ) : (
          <span
            className="flex h-9 w-9 items-center justify-center rounded-input border border-dashed border-warning/50 text-[9px] font-semibold uppercase leading-none text-warning"
            title="This category has no icon yet"
            data-testid={`category-icon-missing-${category.key}`}
          >
            Add
          </span>
        )}
        <div className="flex min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-heading text-data font-bold">{category.label}</span>
            {!category.is_active && (
              <Badge tone="neutral" size="sm" dot data-testid={`category-retired-${category.key}`}>
                Retired
              </Badge>
            )}
          </span>
          {/*
            The code, shown and never editable. It is what sits in every product row, in
            every export and in the API — worth seeing, and not worth being able to break.
          */}
          <span className="truncate font-mono text-micro text-muted-foreground" title="Used in product records and exports">
            {category.key}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-data" data-testid={`category-usage-${category.key}`}>
            {usage_summary(category)}
          </span>
          {used > 0 && (
            <Link
              to={`/catalogue?category=${category.key}`}
              className="w-fit text-micro text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
            >
              See them in the catalogue
            </Link>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => on_open(!open)}
            data-testid={`category-edit-${category.key}`}
            aria-expanded={open}
          >
            {open ? (
              <>
                <X className="mr-1 h-4 w-4" /> Close
              </>
            ) : (
              /*
                "Rename" undersold it once the panel also took the icon — an admin looking for
                somewhere to upload artwork would never think to click it. The label says which
                job is outstanding, so the 13 categories still missing one advertise the work.
              */
              <>
                <Pencil className="mr-1 h-4 w-4" />
                {category.icon_path ? 'Edit' : 'Add icon'}
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={update.isPending}
            onClick={() => void set_active(!category.is_active)}
            data-testid={`category-toggle-${category.key}`}
          >
            <Undo2 className="mr-1 h-4 w-4" />
            {category.is_active ? 'Retire' : 'Bring back'}
          </Button>
          {/*
            Only ever offered when nothing is behind it. The server refuses either way, but
            a button that always fails is worse than no button.
          */}
          {used === 0 && (
            <Button
              size="sm"
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => void destroy()}
              data-testid={`category-delete-${category.key}`}
              aria-label={`Delete ${category.label}`}
            >
              {remove.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 text-destructive" />
              )}
            </Button>
          )}
        </div>
      </div>

      {open && <RenameRow category={category} on_done={() => on_open(false)} />}
    </li>
  );
}

/**
 * Renaming, which is the only edit a category has.
 *
 * The code stays put and the panel says so, because the natural assumption is that
 * renaming changes both — and if it did, every product filed under the old code would
 * quietly stop matching anything.
 */
function RenameRow({
  category,
  on_done,
}: {
  category: CategoryWithUsage;
  on_done: () => void;
}) {
  const update = use_update_category();
  const upload = use_upload_category_icon();
  const file_input = useRef<HTMLInputElement>(null);
  const [label, set_label] = useState(category.label);
  const [error, set_error] = useState<string | null>(null);

  async function save() {
    set_error(null);
    if (!label.trim()) return set_error('Give the category a name.');
    try {
      /*
       * One save for both. Uploading the artwork and then having to press Save separately is
       * how a half-finished category ends up with a name change applied and no icon.
       */
      const file = file_input.current?.files?.[0];
      const icon = file ? await upload.mutateAsync(file) : null;
      const renamed = label.trim() !== category.label;
      await update.mutateAsync({
        id: category.id,
        input: {
          label: label.trim(),
          ...(icon ? { icon_s3_key: icon.icon_s3_key } : {}),
        },
      });
      notify.success(
        renamed ? `Renamed to ${label.trim()}` : `${category.label} updated`,
        {
          description: icon
            ? 'The new icon is live in the shop.'
            : 'Every product filed under it now reads the new name.',
        },
      );
      on_done();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Could not rename this category.');
    }
  }

  return (
    <div
      className="border-t border-border bg-muted/30 px-3.5 py-3"
      data-testid={`category-editor-${category.key}`}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor={`label-${category.id}`}>Name</Label>
          <input
            id={`label-${category.id}`}
            autoFocus
            className="console-field w-64"
            value={label}
            onChange={(e) => set_label(e.target.value)}
            data-testid={`category-label-${category.key}`}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor={`icon-${category.id}`}>
            {category.icon_path ? 'Replace icon' : 'Add an icon'}
          </Label>
          <input
            id={`icon-${category.id}`}
            ref={file_input}
            type="file"
            accept="image/*"
            className="console-field w-64 text-micro"
            data-testid={`category-icon-input-${category.key}`}
          />
          {!category.icon_path && (
            <span className="text-micro text-warning">
              Without one the shop shows a placeholder here.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => void save()}
            disabled={update.isPending}
            data-testid={`category-save-${category.key}`}
          >
            {update.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            Save
          </Button>
          <Button size="sm" variant="ghost" onClick={on_done} disabled={update.isPending}>
            Cancel
          </Button>
        </div>
      </div>
      <p className="mt-2 text-micro text-muted-foreground">
        The code stays <span className="font-mono">{category.key}</span> — it is written into
        every product already filed here, and changing it would detach all of them.
      </p>
      {error && (
        <p className="mt-1 text-micro font-medium text-destructive" data-testid={`category-error-${category.key}`}>
          {error}
        </p>
      )}
    </div>
  );
}

/* --------------------------------- adding --------------------------------- */

function CreateRow({ vertical, on_done }: { vertical: Vertical; on_done: () => void }) {
  const create = use_create_category();
  const upload = use_upload_category_icon();
  const file_input = useRef<HTMLInputElement>(null);
  const [label, set_label] = useState('');
  const [error, set_error] = useState<string | null>(null);
  const key = key_from_label(label);

  async function save() {
    set_error(null);
    if (!label.trim()) return set_error('Give the category a name.');
    try {
      /*
       * Optional here on purpose. Refusing to create a category without artwork would block
       * the quick "we need a SUPPLEMENTS category right now" case on finding a designer, and
       * the list already nags for anything missing one.
       */
      const file = file_input.current?.files?.[0];
      const icon = file ? await upload.mutateAsync(file) : null;
      const made = await create.mutateAsync({
        vertical,
        label: label.trim(),
        icon_s3_key: icon?.icon_s3_key ?? null,
      });
      notify.success(`${made.label} added`, {
        description: 'Vendors can list under it now. Set what it charges on Platform fees.',
      });
      on_done();
    } catch (err) {
      set_error(err instanceof Error ? err.message : 'Could not add this category.');
    }
  }

  return (
    <div className="border-b border-border bg-muted/30 px-3.5 py-3" data-testid="category-create">
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="new-category">Name it</Label>
          <input
            id="new-category"
            autoFocus
            className="console-field w-64"
            placeholder="Grooming tools"
            value={label}
            onChange={(e) => set_label(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save();
            }}
            data-testid="category-new-label"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="new-category-icon">Icon (optional)</Label>
          <input
            id="new-category-icon"
            ref={file_input}
            type="file"
            accept="image/*"
            className="console-field w-64 text-micro"
            data-testid="category-new-icon"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => void save()}
            disabled={create.isPending || upload.isPending}
            data-testid="category-create-save"
          >
            {create.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1 h-4 w-4" />
            )}
            Add to {VERTICAL_LABEL[vertical]}
          </Button>
          <Button size="sm" variant="ghost" onClick={on_done} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </div>
      {/*
        The derived code, shown before saving. Not editable — an admin should not have to
        invent it, and they cannot change it afterwards, so offering the field would be a
        trap. Seeing it still matters: it is what appears in exports and in the API.
      */}
      <p className="mt-2 text-micro text-muted-foreground">
        {key ? (
          <>
            Its code will be <span className="font-mono">{key}</span>, which is what appears in
            exports and the API. It cannot be changed later; the name can.
          </>
        ) : (
          'Its code is worked out from the name.'
        )}
      </p>
      {error && (
        <p className="mt-1 text-micro font-medium text-destructive" data-testid="category-create-error">
          {error}
        </p>
      )}
    </div>
  );
}
